import React, { Fragment, useRef, useState, useEffect, useContext } from 'react';
import { AuthContext } from "../contexts/auth";
import { Dialog, Transition } from '@headlessui/react';
import ComboboxAutocomplete from './combobox_autocomplete';
import { listOrganizationalUnits } from "../services/api_org_unit";
import { TrashIcon } from '@heroicons/react/24/outline';

const OuSelectionPairDWH = ({ onClose, dailyWh, onSelectOUtoPair }) => {
    const { token } = useContext(AuthContext);
    const cancelButtonRef = useRef(null);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [tempSelectedUnit, setTempSelectedUnit] = useState(null);
    const [units, setUnits] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [associatedUnitName, setAssociatedUnitName] = useState(null);   
    
    useEffect(() => {
        async function fetchData() {
            try {
                setIsLoading(true);
                const unitsData = await listOrganizationalUnits(token, true);
                
                const updatedUnits = [{ id: null, name: 'Nessuna Unità' }, ...unitsData.data];
                setUnits(updatedUnits);
                
                if (dailyWh?.dailyWh?.ref_ou) {
                    const matchedUnit = updatedUnits.find(unit => unit.id === dailyWh?.dailyWh?.ref_ou);
                    setAssociatedUnitName(matchedUnit ? matchedUnit.name : dailyWh?.ref_ou);
                }
    
                setIsLoading(false);
            } catch (error) {
                console.error('Error fetching units:', error);
                setError(true);
                setIsLoading(false);
            }
        }
        fetchData();
    }, [token, dailyWh]);    

    const handleSelectUnit = (selected) => {        
        if (selected?.id === null) {
            setTempSelectedUnit({id: null, name: "Nessuna unità"});
        } else {
            setTempSelectedUnit(selected);
        }
    };
    

    const handleConfirmUnit = () => {
        setSelectedUnit(tempSelectedUnit);
        onSelectOUtoPair(tempSelectedUnit, dailyWh?.dailyWh?.dailyWorkingHourId, dailyWh?.employee);
        onClose();
    };
    
    const handleDeleteSelectedUnit = () => {
        setTempSelectedUnit(null);
    };

    return (
        <Transition.Root show={true} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 overflow-y-auto" onClose={() => {}} initialFocus={cancelButtonRef} style={{ zIndex: '999' }}>
                <div className="flex items-start justify-center min-h-screen px-4 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
                    </Transition.Child>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-2/3 sm:w-1/3 mt-20 sm:mt-30 top-0">
                            <div className="text-center flex items-center justify-center bg-indigo-200 p-2">
                                <Dialog.Title as="h3" className="text-lg font-semibold text-indigo-700">
                                    Seleziona l'Unità Organizzativa
                                </Dialog.Title>
                            </div>

                            <div className="p-6">
                                <div className='grid grid-cols-2 gap-2 w-full px-3'>                                            
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase'>
                                        Unità Associata: 
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {associatedUnitName ? (
                                            <div className="text-sm text-gray-600">{associatedUnitName}</div>
                                        ) : (
                                            <span>Nessuna Unità</span>
                                        )}
                                    </div>
                                </div>

                                {!tempSelectedUnit ? (
                                    <div className='my-3 pb-5 bg-white w-full px-3' style={{ zIndex: '25'}}>
                                        <h3 className="py-2 mt-4 font-semibold text-sm text-indigo-800 uppercase">
                                            Seleziona l'Unità Organizzativa da associare al turno
                                        </h3>
                                        {isLoading ? (
                                            'Caricamento...'
                                        ) : (
                                            <div className='border border-gray-300 rounded-md'>
                                                <ComboboxAutocomplete
                                                    options={units}
                                                    bgActiveColor="bg-indigo-100"
                                                    textActiveColor="text-gray-900"
                                                    placeholder="Seleziona l'Unità Organizzativa"
                                                    onChange={handleSelectUnit}
                                                    value={tempSelectedUnit}
                                                    positionOptions='relative'
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="my-4 flex justify-between items-center bg-gray-50 p-3 rounded-md">
                                        <span className="text-sm font-medium text-gray-700 uppercase align-center">
                                            Unità selezionata: <span className='font-normal text-gray-500'>{tempSelectedUnit.name}</span>
                                        </span>
                                        <button className="text-gray-700 font-bold px-1" title="Elimina" onClick={handleDeleteSelectedUnit}>
                                            <TrashIcon className="h-6 w-6 hover:text-red-500" /> 
                                        </button>
                                    </div>
                                )}
                                
                                <div className="text-center flex justify-center">
                                    <button
                                        className="bg-indigo-600 mb-2 text-white text-sm px-4 py-2 rounded-md hover:bg-indigo-500 font-semibold me-3 w-32"
                                        onClick={handleConfirmUnit}
                                    >
                                        Conferma
                                    </button>
                                    <button
                                        onClick={onClose}
                                        className="px-4 mb-2 py-2 rounded-md font-semibold text-sm bg-white border border-solid-2 border-gray-200 shadow-md text-gray-700 w-32"
                                    >
                                        Chiudi
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default OuSelectionPairDWH;
